<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
    contentTag: {
        type: String,
        default: 'div'
    },
    containerClass: {
        type: String,
        default: 'relative bg-app-surface0 rounded-md shadow-sm border overflow-hidden border-slate-200 dark:border-slate-800'
    },
    contentClass: {
        type: String,
        default: ''
    },
    collapsedHeight: {
        type: String,
        default: '20em'
    },
    watchKey: {
        type: [String, Number, Boolean, Object, Array],
        default: null
    },
    seeMoreLabel: {
        type: String,
        default: 'See more'
    },
    seeLessLabel: {
        type: String,
        default: 'See less'
    }
});

const contentRef = ref(null);
const expanded = ref(false);
const needsExpansion = ref(false);
let resizeObserver = null;

const resetExpansionState = () => {
    expanded.value = false;
    needsExpansion.value = false;
};

const collapsedStyle = computed(() => {
    if (!needsExpansion.value || expanded.value) return {};

    return {
        maxHeight: props.collapsedHeight,
        overflow: 'hidden'
    };
});

const checkExpansion = async () => {
    const el = contentRef.value;
    if (!el) return;

    await nextTick();

    const fullHeight = el.scrollHeight;
    const previousMaxHeight = el.style.maxHeight;
    const previousOverflow = el.style.overflow;

    el.style.maxHeight = props.collapsedHeight;
    el.style.overflow = 'hidden';

    const collapsedHeightPx = el.clientHeight;
    const shouldExpand = fullHeight > collapsedHeightPx + 1;

    el.style.maxHeight = previousMaxHeight;
    el.style.overflow = previousOverflow;

    needsExpansion.value = shouldExpand;
    if (!shouldExpand) {
        expanded.value = true;
    }
};

const toggleExpansion = () => {
    expanded.value = !expanded.value;
};

onMounted(async () => {
    await checkExpansion();

    window.addEventListener('resize', checkExpansion);

    if (typeof ResizeObserver !== 'undefined' && contentRef.value) {
        resizeObserver = new ResizeObserver(() => {
            checkExpansion();
        });
        resizeObserver.observe(contentRef.value);
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', checkExpansion);

    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
});

watch(
    () => props.watchKey,
    async () => {
        // Content changed: reset UI state, then re-measure from scratch.
        resetExpansionState();
        await nextTick();
        checkExpansion();
    }
);

watch(
    () => props.collapsedHeight,
    () => {
        checkExpansion();
    }
);
</script>

<template>
    <div :class="containerClass">
        <component
            :is="contentTag"
            ref="contentRef"
            :class="[contentClass, 'transition-all duration-300']"
            :style="collapsedStyle"
        >
            <slot />

            <a
                v-if="needsExpansion && expanded"
                @click="toggleExpansion"
                class="w-full pb-3 text-sm font-medium text-ocean-400 hover:underline cursor-pointer inline-flex items-center gap-2"
                style="z-index: 2;"
            >
                {{ seeLessLabel }}
                <font-awesome-icon icon="fa-solid fa-chevron-up" class="text-xs" />
            </a>
        </component>

        <div
            v-if="needsExpansion && !expanded"
            class="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white dark:from-black via-white/90 dark:via-black/90 to-transparent pointer-events-none"
            style="z-index: 1;"
        ></div>

        <a
            v-if="needsExpansion && !expanded"
            @click="toggleExpansion"
            class="absolute bottom-0 left-0 right-0 px-4 pt-1 pb-3 text-sm font-medium text-ocean-400 hover:underline cursor-pointer inline-flex items-center gap-2"
            style="z-index: 2;"
        >
            {{ seeMoreLabel }}
            <font-awesome-icon icon="fa-solid fa-chevron-down" class="text-xs" />
        </a>
    </div>
</template>
